import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import GoalButton from "./components/GoalButton";
import SuiInput from "components/SuiInput";
import ErrorAlert from "components/ErrorAlert";
import Pagination from "utils/Pagination";
import useGoals from "./useGoals";
import useGoalTemplates from "./useGoalTemplates";
import GoalsEngineersTable from "./components/GoalsEngineersTable";
import GoalExportDialog from "./components/GoalExportDialog";

const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 300;

function Goals() {
  const {
    auth: { admin, team, channel },
  } = useSelector((state) => state);

  const navigate = useNavigate();
  const goalsHook = useGoals({ adminId: admin?.id, teamId: team?.id, channelId: channel?.id });

  const {
    engineers,
    total,
    isLoading,
    errorMessage,
    successMessage,
    fetchEngineers,
    exportGoals,
    clearError,
    clearSuccess,
  } = goalsHook;

  const { templates, fetchTemplates } = useGoalTemplates(admin?.id);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const openExport = () => {
    fetchTemplates();
    setIsExportOpen(true);
  };

  // One effect owns fetching, so search and paging can't disagree about which
  // page/term is on screen. Typing is debounced so it isn't a request per key.
  useEffect(() => {
    const timer = setTimeout(
      () => fetchEngineers({ search: searchTerm, pageNumber: currentPage, pageLimit: PAGE_LIMIT }),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [admin?.id, team?.id, channel?.id, searchTerm, currentPage]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Setting up and reviewing goals both happen on the engineer's details page.
  // `startSetup` opens the setup form as soon as the page loads.
  const openEngineer = (engineer, { startSetup = false } = {}) =>
    navigate(`/goals/${engineer.user_channel_id}`, { state: { startSetup } });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {errorMessage && <ErrorAlert color="error" message={errorMessage} clearError={clearError} />}
      {successMessage && (
        <ErrorAlert color="success" message={successMessage} clearError={clearSuccess} />
      )}

      <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <SuiTypography variant="h5" fontWeight="medium">
          Goals
        </SuiTypography>
        <SuiBox display="flex" gap={1}>
          <GoalButton variant="secondary" icon="download" onClick={openExport}>
            Export
          </GoalButton>
          <GoalButton variant="primary" icon="tune" onClick={() => navigate("/goals/templates")}>
            Manage Templates
          </GoalButton>
        </SuiBox>
      </SuiBox>

      <SuiBox mb={3}>
        <SuiInput
          fullWidth
          placeholder="Search engineer…"
          value={searchTerm}
          onChange={handleSearch}
        />
      </SuiBox>

      <SuiBox mb={3}>
        <GoalsEngineersTable
          engineers={engineers}
          isLoading={isLoading}
          onViewDetails={openEngineer}
        />
      </SuiBox>

      {!isLoading && (
        <SuiBox mb={6}>
          <Pagination
            className="pagination-bar"
            currentPage={currentPage}
            totalCount={total}
            pageSize={PAGE_LIMIT}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </SuiBox>
      )}

      <GoalExportDialog
        open={isExportOpen}
        templates={templates}
        onExport={exportGoals}
        onClose={() => setIsExportOpen(false)}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Goals;
